// Angular imports
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';

// RxJS imports
import { Subscription } from 'rxjs';

// 3rd party imports
import { DxMenuComponent } from 'devextreme-angular';

// CC-Common imports
import { Utils } from 'cc-common';

// Service imports
import { HelperService } from '../../_services/shared-services/helper.service';
import { NavigationService } from '../../_services/shared-services/navigation.service';
import { ContextManagerService } from '../../_services/data-services/context-manager.service';

// Model imports
import { PopMenuInfo } from '../../_models/pop-menu-info';
import { SideNavTreeInfo } from '../../_models/side-nav-tree-info';
import { PropertyCommunityInfoResultDto } from '../../_models/property-community-info-result-dto';

// Constant imports
import { SideNavigationConstants } from '../../_constants/navigation-constants';

@Component({
    selector: 'app-top-navigation-menu',
    templateUrl: './top-navigation-menu.component.html',
    styleUrls: ['./top-navigation-menu.component.scss']
})
export class TopNavigationMenuComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('topMenu', { static: false }) topMenu: DxMenuComponent;
    @Input() showQuickSearch = false;
    @Input() showFooterIcons = false;
    @Input() topMenuItems: SideNavTreeInfo[];
    @Input() bottomMenuItems: SideNavTreeInfo[];
    @Input() selectedNode: SideNavTreeInfo;
    @Input() selectedPath: string;
    @Output() onNavigationChanged = new EventEmitter<any>();

    menuItems: PopMenuInfo[];
    selectedContext: PropertyCommunityInfoResultDto;
    contextSubscription: Subscription;
    navigationSubscription: Subscription;

    searchButtons: any[];
    selectedButton: any;
    searchText = '';
    isSearchPanelAvailable = false;

    hoveredMenu: PopMenuInfo;
    bottomMenuData: PopMenuInfo[];

    constructor(
        private readonly helperService: HelperService,
        private readonly navigationService: NavigationService,
        private readonly contextService: ContextManagerService
    ) {}

    ngOnInit(): void {
        this.searchButtons = [
            { name: 'owner', icon: 'fas fa-house-user', hint: 'Owner Search', selected: true },
            { name: 'community', icon: 'fas fa-map-marker-alt', hint: 'Community Search', selected: false }
        ].map((r) => {
            return {
                icon: r.icon,
                name: r.name,
                hint: r.hint,
                class: 'button-container',
                selected: r.selected
            };
        });
        this.selectedButton = this.searchButtons[0];

        this.updateSelectedContext();
        this.contextSubscription = this.contextService.getContext().subscribe(this.updateSelectedContext.bind(this));

        this.navigationSubscription = this.navigationService
            .onWorkspaceMenus()
            .subscribe((value: { parentId: string; items: SideNavTreeInfo[] }) => {
                setTimeout(() => {
                    if (!!this.topMenu) {
                        this.menuItems = this.helperService.prepareMenuItems(this.topMenuItems);
                        this.topMenu.instance.repaint();
                    }
                }, 100);
            });

        this.menuItems = this.helperService.prepareMenuItems(this.topMenuItems);
        if (!!this.selectedPath) {
            this.selectedNode = this.navigationService.getSelectedSideNavItem(
                this.topMenuItems,
                this.navigationService.getURLPath(this.selectedPath, this.selectedContext)
            );
        }

        if (this.showQuickSearch && this.showFooterIcons) {
            this.navigationService.updateTopMenuStatus(true);
        }

        this.prepareBottomMenuItems();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedPath && !!this.selectedPath) {
            this.selectedNode = this.navigationService.getSelectedSideNavItem(
                this.topMenuItems,
                this.navigationService.getURLPath(this.selectedPath, this.selectedContext)
            );
        }
        if (changes.topMenuItems && !!this.topMenu) {
            this.menuItems = this.helperService.prepareMenuItems(this.topMenuItems);
            this.topMenu.instance.repaint();
        }
        if (changes.bottomMenuItems && changes.bottomMenuItems.currentValue) {
            this.bottomMenuItems = changes.bottomMenuItems.currentValue;
            this.prepareBottomMenuItems();
        }
    }

    private prepareBottomMenuItems(): void {
        const bottomMenuItems = Utils.notNullOrEmpty(this.bottomMenuItems)
            ? this.helperService.prepareMenuItems(this.bottomMenuItems)
            : [];

        bottomMenuItems.forEach((menuItem) => {
            menuItem.visible = menuItem.hasAccess;
        });

        this.bottomMenuData = [
            {
                id: 'bm',
                parentId: null,
                icon: 'fas fa-ellipsis-v',
                name: 'bottom-menu',
                text: '',
                path: null,
                selected: false,
                bookmarked: false,
                disable: false,
                visibleIndex: -1,
                isWorkspace: false,
                items: bottomMenuItems
            }
        ];
    }

    ngOnDestroy(): void {
        if (this.showQuickSearch && this.showFooterIcons) {
            this.navigationService.updateTopMenuStatus(false);
        }
        if (!!this.contextSubscription) {
            this.contextSubscription.unsubscribe();
        }
        if (!!this.navigationSubscription) {
            this.navigationSubscription.unsubscribe();
        }
    }

    isInDevOrQaRelease(item: SideNavTreeInfo): boolean {
        return Utils.isInList(
            item.name.toUpperCase(),
            SideNavigationConstants.QA_ACTION_ACCESS.concat(SideNavigationConstants.IT_ACTION_ACCESS)
        );
    }

    isInQaRelease(item: SideNavTreeInfo): boolean {
        return Utils.isInList(item.name.toUpperCase(), SideNavigationConstants.QA_ACTION_ACCESS);
    }

    isSelected(item: PopMenuInfo): boolean {
        return this.navigationService.isSelectedItem(item, this.selectedNode);
    }

    isLastItem(item: PopMenuInfo): boolean {
        return Utils.equals(
            this.menuItems.findIndex((r) => Utils.equalsIgnoreCase(r.name, item.name)),
            this.menuItems.length - 1
        );
    }

    isTopMenu(item: PopMenuInfo): boolean {
        return Utils.notEquals(
            this.menuItems.findIndex((r) => Utils.equalsIgnoreCase(r.name, item.name)),
            -1
        );
    }

    hideMenuText(item: PopMenuInfo): boolean {
        return (
            Utils.isTrue(this.showQuickSearch) &&
            this.isTopMenu(item) &&
            (Utils.isNullOrUndefined(this.hoveredMenu) || Utils.notEqualsIgnoreCase(item.name, this.hoveredMenu.name))
        );
    }

    onSearchClick(currentButton: any): void {
        this.searchButtons.forEach((button) => (button.selected = false));

        this.selectedButton = currentButton;
        this.selectedButton.selected = true;
        if (this.searchText) {
            this.isSearchPanelAvailable = true;
        } else {
            this.isSearchPanelAvailable = false;
        }
    }

    onTextSearched(searchText: any): void {
        if (searchText) {
            this.searchText = searchText;
            this.isSearchPanelAvailable = true;
        } else {
            this.isSearchPanelAvailable = false;
        }
    }

    onMouseEnter(item: PopMenuInfo): void {
        if (Utils.isTrue(this.showQuickSearch) && this.isTopMenu(item) && Utils.isNullOrEmpty(item.items)) {
            this.hoveredMenu = item;
        }
    }

    onMouseLeave(item: PopMenuInfo): void {
        if (Utils.isTrue(this.showQuickSearch) && this.isTopMenu(item) && Utils.isNullOrEmpty(item.items)) {
            this.hoveredMenu = null;
        }
    }

    onSubmenuShowing(e: any): void {
        if (Utils.isTrue(this.showQuickSearch)) {
            this.menuItems.forEach((item: PopMenuInfo) => {
                if (Utils.notNullAndDefined(e.rootItem.querySelector(`[id=${item.id}]`))) {
                    this.hoveredMenu = item;
                }
            });
        }
    }

    onSubmenuHiding(e: any): void {
        if (Utils.isTrue(this.showQuickSearch) && Utils.isFalse(e.cancel)) {
            this.hoveredMenu = null;
        }
    }

    onMenuItemClick(e: any): void {
        this.hoveredMenu = null;
        this.onNavigationChanged.emit(e);
    }

    onBottomItemClick(item: SideNavTreeInfo): void {
        this.helperService.subjectNavigationChange.next(item.path);
    }

    private updateSelectedContext(): void {
        this.selectedContext = this.contextService.getSelectedContext();
        if (!!this.topMenu) {
            this.menuItems = this.helperService.prepareMenuItems(this.topMenuItems);
            this.topMenu.instance.repaint();
        }
    }
}
